import { Link } from "@tanstack/react-router";
import { FlaskConicalIcon } from "lucide-react";
import { forwardRef, useMemo } from "react";

import { StatIndicator } from "@/components/Indicators";
import { Badge } from "@/components/ui/badge";
import {
  ListItem,
  ListItemActions,
  ListItemLeft,
  ListItemRight,
} from "@/components/ui/list-item";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import { format } from "@/lib/utils";
import type { TestRunNew } from "../types";
import { getProgressValuesFromCounts } from "../utils";
import { TestProgressBar } from "./TestProgressBar";
import { TestTypeBadges } from "./TestTypeBadges";

export const TestRunListItem = forwardRef<
  HTMLAnchorElement,
  { run: TestRunNew; className?: string }
>(({ run, ...props }, ref) => {
  const values = useMemo(
    () => getProgressValuesFromCounts(run.counts ?? undefined),
    [run],
  );

  const hasStat = useMemo(
    () => run.testOrders.some((order) => order.accession.priority === "STAT"),
    [run.testOrders],
  );

  return (
    <ListItem className={cn("h-11", props.className)} asChild>
      <Link
        {...props}
        ref={ref}
        to={run.type !== "PROCEDURAL" ? "/runs/$id" : "/runs/$id/workspace"}
        params={{ id: run.id }}
        state={{ href: { to: "/runs" }, selected: run.id }}
      >
        <ListItemLeft className="gap-4">
          <Badge className="w-16">{run.id}</Badge>
          <Text mono size="2">
            {format(run.createdAt)}
          </Text>
          {hasStat && <StatIndicator />}
        </ListItemLeft>
        <ListItemRight className="gap-4">
          <TestTypeBadges run={run} />
          <Badge className="w-16 gap-1">
            <FlaskConicalIcon className="shrink-0 size-3" />
            <Text muted size="2">
              {values.total}
            </Text>
          </Badge>
        </ListItemRight>
        <ListItemActions className="hidden lg:block min-w-[200px]">
          <TestProgressBar {...values} />
        </ListItemActions>
      </Link>
    </ListItem>
  );
});

export const TestRunLoadingItem = forwardRef<HTMLDivElement>((_, ref) => (
  <ListItem ref={ref} className="w-full h-11">
    <ListItemLeft className="gap-3">
      <Skeleton className="w-14" />
      <Skeleton className="w-32" />
    </ListItemLeft>
    <ListItemRight>
      <Skeleton className="w-20 hidden md:block" />
      <Skeleton className="w-20 md:w-32" />
    </ListItemRight>
    <ListItemActions className="hidden lg:flex min-w-[200px]">
      <Skeleton className="w-full h-2" />
    </ListItemActions>
  </ListItem>
));
